import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { useParams, withRouter } from 'react-router-dom';
import AppShell from '~/components/shared/AppShell/AppShell';
import Card from '~/components/shared/Card/Card';
import ErrorTrackingCode from '~/components/shared/ErrorTrackingCode/ErrorTrackingCode';
import SpinnerCircular from '~/components/shared/Spinner/SpinnerCircular';
import Stepper from '~/components/shared/Stepper/Stepper';
import { FlexContentPlacement, GlobalErrorType, StepStatus } from '~/enums';
import { RootState } from '~/store';
import { trackCardOfferActions } from '~/store/actions/analyticsActions';
import {
  fetchCardOfferData,
  postCardOffer,
  postKYCAnswers,
} from '~/store/actions/cardOfferActions';
import { useCardOfferSelectors } from '~/store/selectors/cardOffer.selector';
import { goToPreviousStep, setApiStatus } from '~/store/slices/cardOffer.slice';
import { ApiStatus } from '~/store/types/sharedTypes';
import { StepsContainer } from '~/styles/BaseStyledComponents';
import { theme } from '~/styles/themes';
import { H5, useModals, useWindowProperties } from '@qred/components-library';
import useTranslate from '~/hooks/useTranslate';
import { toggleGlobalErrorOn } from '~/store/slices/globalError.slice';
import Navbar from '../../components/shared/Navbar/Navbar';
import { TopFixedContainer } from '../../components/shared/TopFixedContainer/TopFixedContainer';
import { TermsAndConditions } from './components/TermsAndConditions/TermsAndConditions';
import WelcomeModalContent from './components/WelcomeModalContent/WelcomeModalContent';
import { useGoToNextStepCardOffer } from './hooks/useGoToNextStepCardOffer';

const CardOffer = () => {
  const dispatch = useAppDispatch();

  const onNextStepClick = useGoToNextStepCardOffer();
  const t = useTranslate();
  const modals = useModals();
  const { isMobile } = useWindowProperties();

  const { offerKey } = useParams<{ offerKey: string }>();
  const {
    cardOffer: { currentStep, apiStatus, overview },
    intl: { market },
    matchedMedia,
  } = useSelector((state: RootState) => state);
  const { stepConfig } = useCardOfferSelectors();
  const currentStepConfig = stepConfig[currentStep - 1];
  const currentStepName = currentStepConfig?.name;

  const { applicationUuid, clientId } = overview;
  useEffect(() => {
    if (offerKey) {
      dispatch(fetchCardOfferData(offerKey));
    }
  }, [offerKey]);

  useEffect(() => {
    if (currentStepName) {
      dispatch(
        trackCardOfferActions({
          stepStatus: StepStatus.Started,
        })
      );
    }
  }, [currentStepName]);

  useEffect(() => {
    if (apiStatus.offer === ApiStatus.Completed) {
      setTimeout(() => {
        modals.openModal({
          withCloseButton: false,
          size: isMobile ? 'sm' : 'lg',
          children: <WelcomeModalContent />,
        });
      }, 500);
    }
  }, [apiStatus.offer]);

  // TODO: Break up into multiple functions for readability
  const confirmButtonHandler = async () => {
    dispatch(
      trackCardOfferActions({
        actionName: 'confirm_button_pressed',
      })
    );

    try {
      await dispatch(postKYCAnswers(clientId));
    } catch {
      // KYC save failed (popup already shown by the thunk); block signing.
      return;
    }

    dispatch(
      trackCardOfferActions({
        actionName: 'kyc_answers_submitted',
      })
    );

    if (applicationUuid && market && clientId) {
      const cardOfferPayload = {
        market,
        clientId,
      };

      dispatch(
        postCardOffer(
          {
            ...cardOfferPayload,
          },
          (data: { signatureUrl: string }) => {
            if (data && data.signatureUrl) {
              window.location.assign(data.signatureUrl);
              dispatch(setApiStatus({ post: ApiStatus.Completed }));
            } else {
              // We shouldn't end up here, maybe we shoud log something to Sentry in this case?
              dispatch(toggleGlobalErrorOn(GlobalErrorType.API_FAILURE));
              dispatch(setApiStatus({ post: ApiStatus.Failed }));
            }
          }
        )
      );
    }
  };

  const stepIsLoading = useMemo(
    () =>
      apiStatus.post === ApiStatus.Started ||
      apiStatus.postKycAnswers === ApiStatus.Started,
    [apiStatus.post, apiStatus.postKycAnswers]
  );
  const onBackButtonClick = () => {
    dispatch(trackCardOfferActions({ actionName: 'back_button_pressed' }));
    dispatch(goToPreviousStep());
    dispatch(trackCardOfferActions({ stepStatus: StepStatus.Edit }));
  };

  const isLastStep = stepConfig.length === currentStep;

  const extraFooterContent = useMemo(() => {
    if (isLastStep) {
      return <TermsAndConditions />;
    }
    return undefined;
  }, [isLastStep]);

  return (
    <AppShell>
      <TopFixedContainer
        style={{ maxWidth: '800px', width: '100%' }}
        topFixedDisabled={!matchedMedia.smallScreenPortrait}
      >
        <Navbar
          onBackClick={onBackButtonClick}
          showBackButton={currentStep > 1}
          progressStepperDots={{
            steps: stepConfig.map((step) => ({
              name: step.name,
              hideInProgressStepper: step.hideInProgressStepper,
            })),
            currentStep,
          }}
        />
      </TopFixedContainer>
      <StepsContainer>
        {
          {
            [ApiStatus.Idle]: (
              <SpinnerCircular color={theme.colors.secondaryGray} />
            ),
            [ApiStatus.Started]: (
              <SpinnerCircular color={theme.colors.secondaryGray} />
            ),
            [ApiStatus.Completed]: (
              <Card
                justifyContent={FlexContentPlacement.SpaceBetween}
                grow={matchedMedia.smallScreenPortrait}
              >
                <Stepper
                  currentStepConfig={currentStepConfig}
                  stepIsLoading={stepIsLoading}
                  currentStep={currentStep}
                  footerCTAAction={
                    isLastStep ? confirmButtonHandler : onNextStepClick
                  }
                  footerCTALabel={
                    isLastStep
                      ? 'ConfirmTermsCard.ConfirmAgreement'
                      : 'OnboardingOffer.Next'
                  }
                  extraFooterContent={extraFooterContent}
                />
              </Card>
            ),
            [ApiStatus.Failed]: (
              <H5>
                {t('OnboardingOffer.NoOfferFound')}
                <ErrorTrackingCode />
              </H5>
            ),
          }[apiStatus.offer]
        }
      </StepsContainer>
    </AppShell>
  );
};

export default withRouter(CardOffer);
